/**
 * MapLegend — color-coded site legend for multi-site map surfaces.
 * Renders one row per site with the lettered marker badge (A/B/C…),
 * the matching siteColorForIndex colour, and the site name. Designed
 * to be absolute-positioned over the map (the consumer owns the
 * positioning + chrome).
 *
 * Pass `map` to make the rows clickable: selecting one flies the map to that
 * site. A row for a site with no coordinates stays inert, since there is
 * nowhere to fly to. `onSiteSelect` runs alongside the fly-to for consumers
 * that want to react as well.
 *
 * Linear: MYD-94 (I3).
 */

import type maplibregl from 'maplibre-gl'
import { letterForIndex } from './SiteMarkers'
import { siteColorForIndex } from '../config/mapConfig'
import { flyToSite, hasPosition } from '../services/mapNavigation'
import { cn } from 'lib/utils'

export interface MapLegendSite {
	_id?: string
	id?: string
	name?: string
	assetName?: string
	latitude?: number | null
	longitude?: number | null
}

export interface MapLegendProps {
	sites: MapLegendSite[]
	title?: string
	className?: string
	/** Supply the map to make rows clickable — selecting one zooms to that site. */
	map?: maplibregl.Map | null
	/** Zoom level to fly to. Defaults to the shared SITE_ZOOM. */
	zoom?: number
	onSiteSelect?: (site: MapLegendSite, index: number) => void
}

export function MapLegend({
	sites,
	title = 'Sites',
	className,
	map,
	zoom,
	onSiteSelect,
}: MapLegendProps) {
	if (sites.length === 0) return null

	const interactive = Boolean(map || onSiteSelect)

	return (
		<div
			className={cn(
				'rounded-md border border-gray-200 bg-white/95 backdrop-blur shadow-sm px-3 py-2.5',
				className,
			)}
		>
			<h5 className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
				{title}
			</h5>
			<ul className="space-y-1">
				{sites.map((site, idx) => {
					const id = site._id ?? site.id ?? String(idx)
					const name = site.name ?? site.assetName ?? '—'
					const canFly = hasPosition(site)
					const clickable = interactive && (canFly || Boolean(onSiteSelect))

					const badge = (
						<span
							className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold text-white shrink-0"
							style={{ background: siteColorForIndex(idx) }}
						>
							{letterForIndex(idx)}
						</span>
					)

					return (
						<li key={id} className="text-xs text-gray-700">
							{clickable ? (
								<button
									type="button"
									onClick={() => {
										flyToSite(map, site, zoom)
										onSiteSelect?.(site, idx)
									}}
									title={canFly ? `Zoom to ${name}` : name}
									className="flex w-full items-center gap-2 rounded px-1 py-0.5 -mx-1 text-left transition-colors hover:bg-[#0080fe]/10 hover:text-[#0080fe] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0080fe]/40"
								>
									{badge}
									<span className="truncate">{name}</span>
								</button>
							) : (
								<div className="flex items-center gap-2 px-1 py-0.5 -mx-1">
									{badge}
									<span className="truncate" title={name}>
										{name}
									</span>
								</div>
							)}
						</li>
					)
				})}
			</ul>
		</div>
	)
}

export default MapLegend
